import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { listTemplates, rollTemplateShareId, upsertTemplate } from '@/src/api/template'
import { ShareLinkCell } from '@/src/components/manage/tables/CopyableCell'
import { Badge } from '@/src/components/ui/badge'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { EnabledAction } from '@/src/components/manage/actions/EnabledAction'
import { CreateAction } from '@/src/components/manage/actions/CreateAction'
import { useTranslation } from '@/src/i18n/client'
import { useAuth } from '@/src/components/AuthContextProvider'
import type { Template } from '@/src/types/template.zod'
import { PERMISSIONS } from '@/permission.config'

export const Route = createFileRoute('/_manage/manage/templates/')({
  component: UserTemplatesPage,
})

function UserTemplatesPage() {
  const { t } = useTranslation('manage');
  const queryClient = useQueryClient()
  const { user, isAdmin, permissions } = useAuth()
  const navigate = useNavigate()
  
  // Check permission client-side
  useEffect(() => {
    const hasAccess = permissions.includes(PERMISSIONS.TEMPLATE_MANAGE) || permissions.includes('*') || isAdmin
    if (!hasAccess) {
      navigate({ to: '/not-permitted' })
    }
  }, [permissions, isAdmin, navigate])
  const { data: allTemplates, isLoading, error } = useQuery({
    queryKey: ['template', 'list'],
    queryFn: () => listTemplates({ data: {} }),
  })

  const templates = allTemplates?.filter((t: any) => t.id !== 'default') || []

  const rollShareIdMutation = useMutation({
    mutationFn: (templateId: string) => rollTemplateShareId({ data: { templateId } }),
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ template, enabled }: { template: Template; enabled: boolean }) =>
      upsertTemplate({
        data: {
          id: template.id,
          name: template.name,
          description: template.description ?? null,
          content: template.content,
          undefinedValue: template.undefinedValue ?? null,
          type: template.type,
          visibility: template.visibility,
          usage: template.usage,
          enabled,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template', 'list'] })
    },
  })

  const columns: ColumnConfig<Template>[] = [
    {
      key: 'shareId',
      label: t('table.shareId'),
      width: 'w-24',
      optional: true,
      render: (template) => {
        const ownerId = template.createdBy?.id
        const canRotate = isAdmin || (!!ownerId && ownerId === user?.id)
        return (
          <ShareLinkCell
            shareId={template.shareId}
            reserveActionSpace={true}
            canRotate={canRotate}
            onRotate={
              canRotate
                ? async () => {
                    await rollShareIdMutation.mutateAsync(template.id)
                    await queryClient.invalidateQueries({ queryKey: ['template', 'list'] })
                  }
                : undefined
            }
          />
        )
      },
    },
    {
      key: 'name',
      label: t('table.name'),
    },
    {
      key: 'description',
      label: t('table.description'),
      render: (template) => template.description || '-',
      optional: true,
    },
    {
      key: 'visibility',
      label: t('table.visibility'),
      optional: true,
      render: (template) => (
        <Badge
          variant={
            template.visibility === "PUBLIC"
              ? "default"
              : template.visibility === "ORGANIZATION"
                ? "secondary"
                : "outline"
          }
          className={
            template.visibility === "PUBLIC"
              ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-green-100 text-green-800 border-green-200"
              : template.visibility === "ORGANIZATION"
                ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-blue-100 text-blue-800 border-blue-200"
                : "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-gray-100 text-gray-800 border-gray-200"
          }
        >
          {t(`templates.form.visibilityLabels.${template.visibility}`)}
        </Badge>
      ),
    },
    {
      key: 'usage',
      label: t('table.usage'),
      optional: true,
      render: (template) => (
        <Badge
          variant="outline"
          className="px-2 py-0.5 rounded-full text-xs font-semibold"
        >
          {t(`templates.form.usageLabels.${template.usage ?? 'RESULTS'}`)}
        </Badge>
      ),
    },
    {
      key: 'enabled',
      label: t('table.status'),
      align: 'right',
      render: (template) => (
        <EnabledAction
          enabled={!!template.enabled}
          onToggle={async (enabled) => {
            await toggleEnabledMutation.mutateAsync({ template, enabled })
          }}
        />
      ),
    },
  ];

  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <CreateAction
            to="/manage/templates/$id"
            params={{ id: 'new' }}
            label={t('table.createTemplate')}
          />
        </AdminToolbar.Left>
      </AdminToolbar>
      <div className="bg-background text-foreground">
        {error && (
          <p className="text-destructive">
            {error instanceof Error ? error.message : t('templates.failedToLoadTemplates')}
          </p>
        )}
        <DataTable<Template>
          data={(templates as any)}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage={t('table.noTemplates')}
          actions={(template) => (
            <EditAction
              to="/manage/templates/$id"
              params={{ id: template.id }}
            />
          )}
        />
      </div>
    </div>
  )
}
